import {
  createRequest,
  createPostRequest,
  createPatchRequest,
  createDeleteRequest,
} from 'lp-redux-api'
import * as LS from 'local-storage'
import {
  serializePartnerParams,
  serializeSchoolParams,
  serializeProfileParams,
  serializeArtistParams,
  generateMappedFilters,
  getPortalAuthToken,
  getCurrentSubdomain,
  buildApiUrl,
  delayedRedirectToUrl,
  formatAnnouncementDate,
  serializePostsSort,
  setBookmarkedFilter,
  removeEmptyFilters,
  serializeAttachments,
} from 'utils'
import normalizeRequestFormErrors from 'utils/local/normalize-request-form-errors'
import * as Types from 'types'
import {
  SEARCH_PAGE_SIZE,
  SEARCH_MAP_PAGE_SIZE,
  DISCUSSIONS_PAGE_SIZE,
  DIRECTORY_PAGE_SIZE,
  USER_BOOKMARK_MESSAGING,
  POST_BOOKMARK_MESSAGING,
  COURSE_BOOKMARK_MESSAGING,
  FLAG_MESSAGE,
  MEMBERSHIP_MESSAGE,
  ANNOUNCEMENT_MESSAGE,
  ATTACHMENT_MESSAGE,
  FOLDER_MESSAGE,
} from 'config'
import { isEmpty } from 'lodash'

function learningHubBookmarkMessaging(bookmarkableType, action) {
  if (bookmarkableType === Types.BOOKMARKABLE.USER) {
    return USER_BOOKMARK_MESSAGING[action]
  }
  if (bookmarkableType === Types.BOOKMARKABLE.LEARNING_HUB_COURSE) {
    return COURSE_BOOKMARK_MESSAGING[action]
  }
  return POST_BOOKMARK_MESSAGING[action]
}

// Map actions
export const fetchSearchResults = createRequest(
  'FETCH_SEARCH_RESULTS',
  ({
    query,
    type,
    filters,
    schoolYear,
    sortType = Types.RELEVANCE_SORT_TYPE,
    page = 1,
  }) => {
    const url =
      type === Types.PARTNER_SEARCH_TYPE ? '/partner_search' : '/school_search'
    const mappedFilters = generateMappedFilters({ filters, type, schoolYear })
    const direction = sortType === Types.ALPHABETICAL_SORT_TYPE ? 'asc' : 'desc'

    return {
      url: url,
      query: {
        query,
        page,
        sort: `{ "${sortType}": "${direction}" }`,
        filters: JSON.stringify(mappedFilters),
        limit: SEARCH_PAGE_SIZE,
      },
    }
  }
)

// Fetches every matching record (not just the current page) so the map can
// display all pins for the current search/filters. Hits the lightweight
// `markers` endpoints, which return only id/name/type/address per record.
export const fetchSearchMapLocations = createRequest(
  'FETCH_SEARCH_MAP_LOCATIONS',
  ({ query, type, filters, schoolYear }) => {
    const url =
      type === Types.PARTNER_SEARCH_TYPE
        ? '/partner_search/markers'
        : '/school_search/markers'
    const mappedFilters = generateMappedFilters({ filters, type, schoolYear })

    return {
      url: url,
      query: {
        query,
        page: 1,
        sort: `{ "${Types.ALPHABETICAL_SORT_TYPE}": "asc" }`,
        filters: JSON.stringify(mappedFilters),
        limit: SEARCH_MAP_PAGE_SIZE,
      },
    }
  }
)

export const fetchSearchOptions = createRequest(
  'FETCH_SEARCH_OPTIONS',
  (
    { blockList = [] } = {},
    type = Types.SEARCH_OPTIONS_TYPE.MAP,
    districtId = null
  ) => ({
    url: '/search_options',
    query: {
      blockList: isEmpty(blockList) ? undefined : JSON.stringify(blockList),
      districtId,
    },
    onSuccess: (searchOptions) => ({
      searchOptions: {
        ...searchOptions,
      },
      type,
    }),
  })
)

export const fetchSchool = createRequest(
  'FETCH_SCHOOL',
  (id, schoolYearId) => ({
    url: `/schools/${id}`,
    query: { school_year_id: schoolYearId },
  })
)

export const fetchArtist = createRequest(
  'FETCH_ARTIST',
  (id, schoolYearId) => ({
    url: `/artists/${id}`,
    query: { school_year_id: schoolYearId },
  })
)

export const fetchPartner = createRequest(
  'FETCH_PARTNER',
  (id, schoolYearId) => ({
    url: `/organizations/${id}`,
    query: { school_year_id: schoolYearId },
  })
)

export const fetchSchoolOptions = createRequest('FETCH_SCHOOL_OPTIONS', () => ({
  url: `/schools`,
}))

export const fetchArtistOptions = createRequest('FETCH_ARTIST_OPTIONS', () => ({
  url: `/artists`,
}))

export const fetchPartnerAuthorized = createRequest(
  'FETCH_PARTNER_AUTHORIZED',
  (id, years) => ({
    url: `/partner_portal/organizations/${id}`,
    query: { years },
    token: LS.getPartnerAuthToken(),
  })
)

export const fetchArtistAuthorized = createRequest(
  'FETCH_ARTIST_AUTHORIZED',
  (id, years) => ({
    url: `/artist_portal/artists/${id}`,
    query: { years },
    token: LS.getArtistAuthToken(),
  })
)

export const updatePartner = createPatchRequest(
  'UPDATE_PARTNER',
  (partnerId, schoolYear, partner) => {
    return {
      url: `/partner_portal/organizations/${partnerId}`,
      body: { organization: serializePartnerParams(partner, schoolYear) },
      token: LS.getPartnerAuthToken(),
    }
  }
)

export const updateArtist = createPatchRequest(
  'UPDATE_ARTIST',
  (artistId, schoolYear, artist) => {
    return {
      url: `/artist_portal/artists/${artistId}`,
      body: { artist: serializeArtistParams(artist, schoolYear) },
      token: LS.getArtistAuthToken() || LS.getPartnerAuthToken(),
    }
  }
)

export const fetchProfileCompletion = createRequest(
  'FETCH_PROFILE_COMPLETION',
  (partnerId) => ({
    url: `/partner_portal/organizations/${partnerId}/profile_completion`,
    token: LS.getPartnerAuthToken(),
  })
)

export const updateProfileCompletion = createPatchRequest(
  'UPDATE_PROFILE_COMPLETION',
  (partnerId, { section, percentCompletion }) => ({
    url: `/partner_portal/organizations/${partnerId}/profile_completion`,
    body: {
      profile_completion: {
        sections_completed: section,
        percent_completion: percentCompletion,
      },
    },
    token: LS.getPartnerAuthToken(),
  })
)

export const fetchCurrentPartnerUserDetails = createRequest(
  'FETCH_CURRENT_PARTNER_USER_DETAILS',
  () => {
    return {
      url: '/authentication/current_user_details',
      token: LS.getPartnerAuthToken(),
    }
  }
)

export const fetchCurrentArtistUserDetails = createRequest(
  'FETCH_CURRENT_ARTIST_USER_DETAILS',
  () => {
    return {
      url: '/authentication/current_user_details',
      token: LS.getArtistAuthToken(),
    }
  }
)

// School portal actions
export const fetchSchoolUserDetails = createRequest(
  'FETCH_SCHOOL_USER_DETAILS',
  (userId) => ({
    url: `/school_portal/users/${userId}`,
  })
)

export const fetchPartnerOptions = createRequest(
  'FETCH_PARTNER_OPTIONS',
  () => ({
    url: `/partner_portal/organizations`,
  })
)

export const fetchBadgeQuestions = createRequest(
  'FETCH_BADGE_QUESTIONS',
  () => ({
    url: `/badge_questions`,
  })
)

export const fetchCaCommonCores = createRequest(
  'FETCH_CA_COMMON_CORES',
  () => ({
    url: `/ca_common_cores`,
  })
)

/** Active organizations for current school year (public index). */
export const fetchOrganizationOptions = createRequest(
  'FETCH_ORGANIZATION_OPTIONS',
  () => ({
    url: `/organizations`,
  })
)

export const fetchApplicationQuestions = createRequest(
  'FETCH_APPLICATION_QUESTIONS',
  (applicableType = 'TeachingArtist') => ({
    url: `/application_questions`,
    query: { applicable_type: applicableType },
  })
)

export const fetchSchoolAuthorized = createRequest(
  'FETCH_SCHOOL_AUTHORIZED',
  (id, schoolYearId) => ({
    url: `/school_portal/schools/${id}`,
    query: { school_year_id: schoolYearId },
    token: LS.getSchoolAuthToken(),
  })
)

export const fetchSchoolOnCurrentSchoolYear = createRequest(
  'FETCH_SCHOOL_ON_SCHOOL_YEAR',
  (id, schoolYearId) => ({
    url: `/school_portal/schools/${id}`,
    query: { school_year_id: schoolYearId },
    token: LS.getSchoolAuthToken(),
  })
)

// Dedicated fetch for the school scoped to the *current* school year, used only
// to compute profile completion. This is kept separate from the global school
// (which may be loaded for the survey school year) so that re-saving a section
// never recomputes completion against a different year's data.
export const fetchCompletionSchool = createRequest(
  'FETCH_COMPLETION_SCHOOL',
  (id, schoolYearId) => ({
    url: `/school_portal/schools/${id}`,
    query: { school_year_id: schoolYearId },
    token: LS.getSchoolAuthToken(),
  })
)

export const fetchSpaceTypes = createRequest('FETCH_SPACE_TYPES', () => ({
  url: `/school_portal/space_types`,
  token: LS.getSchoolAuthToken(),
}))

export const fetchArtsEducationResourceTypes = createRequest(
  'FETCH_ARTS_EDUCATION_RESOURCE_TYPES',
  () => ({
    url: `/school_portal/arts_education_resource_types`,
    token: LS.getSchoolAuthToken(),
  })
)

export const fetchInstructionalFrequencyTypes = createRequest(
  'FETCH_INSTRUCTIONAL_FREQUENCY_TYPES',
  () => ({
    url: `/school_portal/instructional_frequency_types`,
    token: LS.getSchoolAuthToken(),
  })
)

export const fetchClassDurationTypes = createRequest(
  'FETCH_CLASS_DURATION_TYPES',
  () => ({
    url: `/school_portal/class_duration_types`,
    token: LS.getSchoolAuthToken(),
  })
)

export const fetchIntegrationTypes = createRequest(
  'FETCH_INTEGRATION_TYPES',
  () => ({
    url: `/school_portal/integration_types`,
    token: LS.getSchoolAuthToken(),
  })
)

export const fetchEngagementTypes = createRequest(
  'FETCH_ENGAGEMENT_TYPES',
  () => ({
    url: `/school_portal/engagement_types`,
    token: LS.getSchoolAuthToken(),
  })
)

export const fetchScheduleTypes = createRequest('FETCH_SCHEDULE_TYPES', () => ({
  url: `/school_portal/schedule_types`,
  token: LS.getSchoolAuthToken(),
}))

export const fetchSurveyAttempt = createRequest(
  'FETCH_SURVEY_ATTEMPT',
  (schoolId) => ({
    url: `/school_portal/survey_attempts`,
    query: { schoolId },
    token: LS.getSchoolAuthToken(),
  })
)

export const completeSurveyAttempt = createPatchRequest(
  'COMPLETE_SURVEY_ATTEMPT',
  (surveyAttemptId) => ({
    url: `school_portal/survey_attempts/${surveyAttemptId}/complete`,
    token: LS.getSchoolAuthToken(),
  })
)

export const fetchSurveyArchives = createRequest(
  'FETCH_SURVEY_ARCHIVES',
  () => ({
    url: `/school_portal/survey_archives`,
    token: LS.getSchoolAuthToken(),
  })
)

export const updateSurveyAttemptState = createPatchRequest(
  'UPDATE_SURVEY_ATTEMPT_STATE',
  (schoolId, surveyAttemptId, state) => {
    return {
      url: `/school_portal/schools/${schoolId}`,
      body: {
        school: {
          surveyAttemptsAttributes: [
            {
              id: surveyAttemptId,
              state,
            },
          ],
        },
      },
      token: LS.getSchoolAuthToken(),
    }
  }
)

export const updateSchool = createPatchRequest(
  'UPDATE_SCHOOL',
  (schoolId, schoolYear, school) => {
    return {
      url: `/school_portal/schools/${schoolId}`,
      body: {
        school: serializeSchoolParams(school, schoolYear),
        school_year_id: schoolYear,
      },
      token: LS.getSchoolAuthToken(),
    }
  }
)

export const fetchFundingTypes = createRequest('FETCH_FUNDING_TYPES', () => ({
  url: `/school_portal/funding_types`,
  token: getPortalAuthToken(),
}))

export const fetchProgramTypes = createRequest('FETCH_PROGRAM_TYPES', () => ({
  url: `/program_types`,
}))

export const fetchLanguages = createRequest('FETCH_LANGUAGES', () => ({
  url: `/languages`,
}))

export const fetchCurrentSchoolUserDetails = createRequest(
  'FETCH_CURRENT_SCHOOL_USER_DETAILS',
  () => {
    return {
      url: '/authentication/current_user_details',
      token: LS.getSchoolAuthToken(),
    }
  }
)

export const fetchSurveyAttemptReview = createRequest(
  'FETCH_SURVEY_ATTEMPT_REVIEW',
  (surveyAttemptId) => ({
    url: `school_portal/survey_attempts/${surveyAttemptId}/review`,
    token: LS.getSchoolAuthToken(),
  })
)

// Educator portal actions

export const fetchCurrentEducatorUserDetails = createRequest(
  'FETCH_CURRENT_EDUCATOR_USER_DETAILS',
  () => {
    return {
      url: '/educator_portal/users/current_user_details',
      token: getPortalAuthToken(),
    }
  }
)

export const createOrUpdateEducatorUserProfile = createRequest(
  'CREATE_OR_UPDATE_EDUCATOR_USER_PROFILE',
  (profileId, schoolYearId, params) => {
    const baseUrl = '/educator_portal/profiles'
    const method = profileId ? 'PATCH' : 'POST'
    const url = profileId ? `${baseUrl}/${profileId}` : baseUrl

    return {
      url,
      method,
      body: serializeProfileParams(params, schoolYearId),
      token: getPortalAuthToken(),
    }
  }
)

export const fetchDirectoryUsers = createRequest(
  'FETCH_DIRECTORY_USERS',
  ({ query = '', page = 1, filters = {} }, bookmarkedUserIds) => {
    const populatedFilters = removeEmptyFilters(filters)
    const filtersWithBookmarks = setBookmarkedFilter(
      populatedFilters,
      bookmarkedUserIds
    )
    return {
      url: 'educator_portal/users/search',
      query: {
        query,
        page,
        filters: JSON.stringify(filtersWithBookmarks),
        sort: `{ "${Types.ALPHABETICAL_SORT_TYPE}": "asc" }`,
        limit: DIRECTORY_PAGE_SIZE,
      },
      token: getPortalAuthToken(),
    }
  }
)

export const fetchDirectoryUser = createRequest(
  'FETCH_DIRECTORY_USER',
  (userId) => ({
    url: `/educator_portal/users/${userId}`,
    token: getPortalAuthToken(),
  })
)

export const addBookmark = createPostRequest(
  'ADD_BOOKMARK',
  ({ bookmarkableId, bookmarkableType }) => ({
    url: '/educator_portal/bookmarks',
    body: {
      bookmark: {
        bookmarkableId,
        bookmarkableType,
      },
    },
    token: getPortalAuthToken(),
    onSuccess: (bookmarks) => {
      const successMessage =
        bookmarkableType === Types.BOOKMARKABLE.USER
          ? USER_BOOKMARK_MESSAGING.ADD.SUCCESS
          : POST_BOOKMARK_MESSAGING.ADD.SUCCESS
      return { bookmarks, successMessage }
    },
    onFailure: (error) => {
      const defaultMessage =
        bookmarkableType === Types.BOOKMARKABLE.USER
          ? USER_BOOKMARK_MESSAGING.ADD.FAIL
          : POST_BOOKMARK_MESSAGING.ADD.FAIL
      return { errorMessage: error.errors || defaultMessage }
    },
  })
)

export const removeBookmark = createDeleteRequest(
  'REMOVE_BOOKMARK',
  ({ bookmarkId, bookmarkableType }) => ({
    url: `/educator_portal/bookmarks/${bookmarkId}`,
    token: getPortalAuthToken(),
    onSuccess: (bookmarks) => {
      const successMessage =
        bookmarkableType === Types.BOOKMARKABLE.USER
          ? USER_BOOKMARK_MESSAGING.REMOVE.SUCCESS
          : POST_BOOKMARK_MESSAGING.REMOVE.SUCCESS
      return { bookmarks, successMessage }
    },
    onFailure: (error) => {
      const defaultMessage =
        bookmarkableType === Types.BOOKMARKABLE.USER
          ? USER_BOOKMARK_MESSAGING.REMOVE.FAIL
          : POST_BOOKMARK_MESSAGING.REMOVE.FAIL
      return { errorMessage: error.errors || defaultMessage }
    },
  })
)

export const addLike = createPostRequest(
  'ADD_LIKE',
  ({ likeableId, likeableType }) => ({
    url: 'educator_portal/likes',
    body: {
      like: {
        likeableId,
        likeableType,
      },
    },
    token: getPortalAuthToken(),
  })
)

export const destroyLike = createDeleteRequest('DESTROY_LIKE', ({ id }) => ({
  url: `/educator_portal/likes/${id}`,
  token: getPortalAuthToken(),
}))

export const fetchPosts = createRequest(
  'FETCH_POSTS',
  ({ query, page = 1, filters, sortType }, bookmarkedPostIds) => {
    const { type, direction } = serializePostsSort(sortType)
    const populatedFilters = removeEmptyFilters(filters)
    const filtersWithBookmarks = setBookmarkedFilter(
      populatedFilters,
      bookmarkedPostIds
    )
    return {
      url: '/educator_portal/posts/search',
      query: {
        query,
        page,
        filters: JSON.stringify(filtersWithBookmarks),
        sort: `{ "${type}": "${direction}" }`,
        limit: DISCUSSIONS_PAGE_SIZE,
      },
      token: getPortalAuthToken(),
    }
  }
)

export const fetchPostTopics = createRequest('FETCH_POST_TOPICS', () => ({
  url: '/educator_portal/topics',
  token: getPortalAuthToken(),
}))

export const fetchLearningHubCourses = createRequest(
  'FETCH_LEARNING_HUB_COURSES',
  ({ query } = {}) => ({
    url: '/learning_hub/courses',
    query: {
      query: query || undefined,
    },
    token: getPortalAuthToken(),
  })
)

export const fetchLearningHubCoursesMore = createRequest(
  'FETCH_LEARNING_HUB_COURSES_MORE',
  ({ disciplineId, offset, limit = 5, uncategorized = false, query }) => ({
    url: '/learning_hub/courses',
    query: uncategorized
      ? { uncategorized: true, offset, limit, query: query || undefined }
      : {
          discipline_id: disciplineId,
          offset,
          limit,
          query: query || undefined,
        },
    token: getPortalAuthToken(),
  })
)

export const fetchLearningHubCourse = createRequest(
  'FETCH_LEARNING_HUB_COURSE',
  (courseId) => ({
    url: `/learning_hub/courses/${courseId}`,
    token: getPortalAuthToken(),
  })
)

export const fetchLearningHubCoursePreview = createRequest(
  'FETCH_LEARNING_HUB_COURSE_PREVIEW',
  (courseId, previewToken) => ({
    url: `/learning_hub/courses/${courseId}/preview`,
    query: { token: previewToken },
  })
)

export const fetchLearningHubBlock = createRequest(
  'FETCH_LEARNING_HUB_BLOCK',
  (blockId) => ({
    url: `/learning_hub/blocks/${blockId}`,
    token: getPortalAuthToken(),
  })
)

// Token-validated read-only block fetch for the admin "Preview Course" flow.
// Mirrors fetchLearningHubBlock but authenticates with the per-course preview
// JWT instead of the learner's portal session.
export const fetchLearningHubBlockPreview = createRequest(
  'FETCH_LEARNING_HUB_BLOCK_PREVIEW',
  (blockId, previewToken) => ({
    url: `/learning_hub/blocks/${blockId}/preview`,
    query: { token: previewToken },
  })
)

export async function submitLearningHubFormResponse(blockId, body) {
  const url = buildApiUrl(`/learning_hub/blocks/${blockId}/form_response`)
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getPortalAuthToken()}`,
      'x-subdomain': getCurrentSubdomain(),
    },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg =
      json.message ||
      json.error ||
      (Array.isArray(json.errors) && json.errors[0]) ||
      'Unable to save form.'
    const err = new Error(msg)
    err.status = res.status
    err.body = json
    throw err
  }
  return json.data
}

// Uploads a learner's media response for an `upload_media` form question.
// Goes through the server (resource_type :auto) so Cloudinary detects the
// asset type correctly for video/audio, rather than the image-only unsigned
// preset that previously rejected mp4 uploads from the browser.
export async function uploadLearningHubFormMedia(blockId, file) {
  const url = buildApiUrl(`/learning_hub/blocks/${blockId}/form_media`)
  const formData = new FormData()
  formData.append('file', file)
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${getPortalAuthToken()}`,
      'x-subdomain': getCurrentSubdomain(),
    },
    body: formData,
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg =
      json.message ||
      json.error ||
      (Array.isArray(json.errors) && json.errors[0]) ||
      'Unable to upload file.'
    const err = new Error(msg)
    err.status = res.status
    err.body = json
    throw err
  }
  return json.data.url
}

export async function saveLearningHubBlockProgress(blockId, progress) {
  const url = buildApiUrl(`/learning_hub/blocks/${blockId}/progress`)
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getPortalAuthToken()}`,
      'x-subdomain': getCurrentSubdomain(),
    },
    body: JSON.stringify({ progress }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg =
      json.message ||
      json.error ||
      (Array.isArray(json.errors) && json.errors[0]) ||
      'Unable to save progress.'
    const err = new Error(msg)
    err.status = res.status
    err.body = json
    throw err
  }
  return json.data
}

export const startLearningHubCourse = createPostRequest(
  'START_LEARNING_HUB_COURSE',
  (courseId) => ({
    url: `/learning_hub/courses/${courseId}/start`,
    token: getPortalAuthToken(),
    onFailure: (error) => ({
      errorMessage:
        error?.errors || error?.message || 'Unable to start this course.',
    }),
  })
)

export const fetchLearningHubMyCourses = createRequest(
  'FETCH_LEARNING_HUB_MY_COURSES',
  () => ({
    url: '/learning_hub/courses/my_courses',
    token: getPortalAuthToken(),
  })
)

export const fetchLearningHubDiscussions = createRequest(
  'FETCH_LEARNING_HUB_DISCUSSIONS',
  ({ query, page = 1, filters, sortType }, bookmarkedDiscussionIds) => {
    const { type, direction } = serializePostsSort(sortType)
    const populatedFilters = removeEmptyFilters(filters)
    const filtersWithBookmarks = setBookmarkedFilter(
      populatedFilters,
      bookmarkedDiscussionIds
    )
    return {
      url: '/learning_hub/discussions/search',
      query: {
        query,
        page,
        filters: JSON.stringify(filtersWithBookmarks),
        sort: `{ "${type}": "${direction}" }`,
        limit: DISCUSSIONS_PAGE_SIZE,
      },
      token: getPortalAuthToken(),
    }
  }
)

export const fetchLearningHubPostTopics = createRequest(
  'FETCH_LEARNING_HUB_POST_TOPICS',
  () => ({
    url: '/learning_hub/topics',
    token: getPortalAuthToken(),
  })
)

export const createOrUpdateLearningHubDiscussion = createRequest(
  'CREATE_OR_UPDATE_LEARNING_HUB_DISCUSSION',
  (post, discussionId) => {
    const {
      title,
      content,
      category,
      type,
      disciplines,
      topics,
      attachments,
      courses,
    } = post
    const baseUrl = '/learning_hub/discussions'
    const method = discussionId ? 'PATCH' : 'POST'
    const url = discussionId ? `${baseUrl}/${discussionId}` : baseUrl

    const courseRows = (courses || [])
      .map((row) => ({ learningHubCourseId: row.learningHubCourseId }))
      .filter((row) => row.learningHubCourseId != null)

    return {
      url,
      method,
      body: {
        discussion: {
          title,
          content,
          category,
          type,
          learningHubDiscussionDisciplinesAttributes: disciplines,
          learningHubDiscussionTopicsAttributes: topics,
          learningHubDiscussionCoursesAttributes: courseRows,
          learningHubAttachmentsAttributes: serializeAttachments(attachments),
        },
      },
      token: getPortalAuthToken(),
    }
  }
)

export const archiveLearningHubDiscussion = createPatchRequest(
  'ARCHIVE_LEARNING_HUB_DISCUSSION',
  (discussionId) => ({
    url: `/learning_hub/discussions/${discussionId}/archive`,
    token: getPortalAuthToken(),
  })
)

export const lockLearningHubDiscussion = createPatchRequest(
  'LOCK_LEARNING_HUB_DISCUSSION',
  (discussionId) => ({
    url: `/learning_hub/discussions/${discussionId}/lock`,
    token: getPortalAuthToken(),
  })
)

export const reopenLearningHubDiscussion = createPatchRequest(
  'REOPEN_LEARNING_HUB_DISCUSSION',
  (discussionId) => ({
    url: `/learning_hub/discussions/${discussionId}/reopen`,
    token: getPortalAuthToken(),
  })
)

export const fetchLearningHubDiscussion = createRequest(
  'FETCH_LEARNING_HUB_DISCUSSION',
  (discussionId) => ({
    url: `/learning_hub/discussions/${discussionId}`,
    token: getPortalAuthToken(),
  })
)

export const createLearningHubDiscussionComment = createPostRequest(
  'CREATE_LEARNING_HUB_DISCUSSION_COMMENT',
  (discussionId, parentId, content, attachments) => ({
    url: '/learning_hub/discussion_comments',
    body: {
      comment: {
        learningHubDiscussionId: discussionId,
        parentId: parentId || undefined,
        content,
        learningHubAttachmentsAttributes: serializeAttachments(attachments),
      },
    },
    token: getPortalAuthToken(),
  })
)

export const updateLearningHubDiscussionComment = createPatchRequest(
  'UPDATE_LEARNING_HUB_DISCUSSION_COMMENT',
  (commentId, content, attachments) => ({
    url: `/learning_hub/discussion_comments/${commentId}`,
    body: {
      comment: {
        content,
        learningHubAttachmentsAttributes: serializeAttachments(attachments),
      },
    },
    token: getPortalAuthToken(),
  })
)

export const fetchLearningHubDiscussionComments = createRequest(
  'FETCH_LEARNING_HUB_DISCUSSION_COMMENTS',
  (discussionId) => ({
    url: `/learning_hub/discussions/${discussionId}/comments`,
    token: getPortalAuthToken(),
  })
)

export const archiveLearningHubDiscussionComment = createPatchRequest(
  'ARCHIVE_LEARNING_HUB_DISCUSSION_COMMENT',
  (commentId) => ({
    url: `/learning_hub/discussion_comments/${commentId}/archive`,
    token: getPortalAuthToken(),
  })
)

export const addLearningHubLike = createPostRequest(
  'ADD_LEARNING_HUB_LIKE',
  ({ likeableId, likeableType }) => ({
    url: '/learning_hub/likes',
    body: {
      like: {
        likeableId,
        likeableType,
      },
    },
    token: getPortalAuthToken(),
  })
)

export const destroyLearningHubLike = createDeleteRequest(
  'DESTROY_LEARNING_HUB_LIKE',
  ({ id }) => ({
    url: `/learning_hub/likes/${id}`,
    token: getPortalAuthToken(),
  })
)

export const addLearningHubBookmark = createPostRequest(
  'ADD_LEARNING_HUB_BOOKMARK',
  ({ bookmarkableId, bookmarkableType }) => ({
    url: '/learning_hub/bookmarks',
    body: {
      bookmark: {
        bookmarkableId,
        bookmarkableType,
      },
    },
    token: getPortalAuthToken(),
    onSuccess: (bookmarks) => {
      const messaging = learningHubBookmarkMessaging(bookmarkableType, 'ADD')
      return { bookmarks, successMessage: messaging.SUCCESS }
    },
    onFailure: (error) => {
      const messaging = learningHubBookmarkMessaging(bookmarkableType, 'ADD')
      return { errorMessage: error.errors || messaging.FAIL }
    },
  })
)

export const removeLearningHubBookmark = createDeleteRequest(
  'REMOVE_LEARNING_HUB_BOOKMARK',
  ({ bookmarkId, bookmarkableType }) => ({
    url: `/learning_hub/bookmarks/${bookmarkId}`,
    token: getPortalAuthToken(),
    onSuccess: (bookmarks) => {
      const messaging = learningHubBookmarkMessaging(bookmarkableType, 'REMOVE')
      return { bookmarks, successMessage: messaging.SUCCESS }
    },
    onFailure: (error) => {
      const messaging = learningHubBookmarkMessaging(bookmarkableType, 'REMOVE')
      return { errorMessage: error.errors || messaging.FAIL }
    },
  })
)

export const createLearningHubForumFlag = createPostRequest(
  'CREATE_LEARNING_HUB_FORUM_FLAG',
  ({ flaggableType, flaggableId, comment }) => ({
    url: '/learning_hub/forum_flags',
    body: {
      forum_flag: {
        flaggable_type: flaggableType,
        flaggable_id: flaggableId,
        comment,
      },
    },
    token: getPortalAuthToken(),
    onFailure: (error) => ({
      errorMessage:
        error.message || error.errors || 'Unable to submit flag for review.',
    }),
  })
)

export const fetchMentionSuggestions = createRequest(
  'FETCH_MENTION_SUGGESTIONS',
  () => ({
    url: '/educator_portal/mention_options',
    token: getPortalAuthToken(),
  })
)

export const createOrUpdatePost = createRequest(
  'CREATE_OR_UPDATE_POST',
  (post, postId, communityOfPracticeId) => {
    // strip out any post attributes that aren't required for submission
    const { title, content, category, disciplines, topics, attachments } = post
    const baseUrl = '/educator_portal/posts'
    const method = postId ? 'PATCH' : 'POST'
    const url = postId ? `${baseUrl}/${postId}` : baseUrl

    return {
      url,
      method,
      body: {
        post: {
          title,
          content,
          category,
          postDisciplinesAttributes: disciplines,
          postTopicsAttributes: topics,
          attachmentsAttributes: serializeAttachments(attachments),
          communityOfPracticeId,
        },
      },
      token: getPortalAuthToken(),
    }
  }
)

export const archivePost = createPatchRequest('ARCHIVE_POST', (postId) => ({
  url: `/educator_portal/posts/${postId}/archive`,
  token: getPortalAuthToken(),
}))

export const lockPost = createPatchRequest('LOCK_POST', (postId) => ({
  url: `/educator_portal/posts/${postId}/lock`,
  token: getPortalAuthToken(),
}))

export const reopenPost = createPatchRequest('REOPEN_POST', (postId) => ({
  url: `/educator_portal/posts/${postId}/reopen`,
  token: getPortalAuthToken(),
}))

export const fetchPost = createRequest('FETCH_POST', (postId) => ({
  url: `/educator_portal/posts/${postId}`,
  token: getPortalAuthToken(),
}))

export const createComment = createPostRequest(
  'CREATE_COMMENT',
  (commentableId, commentableType, content, attachments) => ({
    url: '/educator_portal/comments',
    body: {
      comment: {
        commentableType,
        commentableId,
        content,
        attachmentsAttributes: serializeAttachments(attachments),
      },
    },
    token: getPortalAuthToken(),
  })
)

export const updateComment = createPatchRequest(
  'UPDATE_COMMENT',
  (commentId, content, attachments) => ({
    url: `/educator_portal/comments/${commentId}`,
    body: {
      comment: {
        content,
        attachmentsAttributes: serializeAttachments(attachments),
      },
    },
    token: getPortalAuthToken(),
  })
)

export const fetchComments = createRequest('FETCH_COMMENTS', (postId) => ({
  url: `/educator_portal/posts/${postId}/comments`,
  token: getPortalAuthToken(),
}))

export const archiveComment = createPatchRequest(
  'ARCHIVE_COMMENT',
  (commentId) => ({
    url: `/educator_portal/comments/${commentId}/archive`,
    token: getPortalAuthToken(),
  })
)

export const fetchFlaggedItems = createRequest('FETCH_FLAGGGED_ITEMS', () => ({
  url: '/educator_portal/flags',
  token: getPortalAuthToken(),
}))

export const createFlag = createPostRequest(
  'CREATE_FLAG',
  ({ reviewableId, reviewableType, reason }) => ({
    url: '/educator_portal/flags',
    body: {
      flag: {
        reviewableType,
        reviewableId,
        reason,
      },
    },
    token: getPortalAuthToken(),
    onSuccess: (reviewable) => {
      const successMessage = FLAG_MESSAGE.CREATE.SUCCESS
      return { reviewable, successMessage }
    },
    onFailure: (error) => {
      const defaultMessage = FLAG_MESSAGE.CREATE.FAIL
      return { errorMessage: error.errors || defaultMessage }
    },
  })
)

export const unflag = createPatchRequest('UNFLAG', (id) => ({
  url: `/educator_portal/flags/${id}/unflag`,
  token: getPortalAuthToken(),
  onSuccess: (reviewable) => {
    const successMessage = FLAG_MESSAGE.UNFLAG.SUCCESS
    return { reviewable, successMessage }
  },
  onFailure: (error) => {
    const defaultMessage = FLAG_MESSAGE.UNFLAG.FAIL
    return { errorMessage: error.errors || defaultMessage }
  },
}))

export const agreeWithFlag = createPatchRequest('AGREE_WITH_FLAG', (id) => ({
  url: `/educator_portal/flags/${id}/agree`,
  body: {
    flag: {
      resolutionNote: Types.FLAG_RESOLUTION_NOTES.AGREE,
    },
  },
  token: getPortalAuthToken(),
  onSuccess: (reviewable) => {
    const successMessage =
      FLAG_MESSAGE.AGREE.SUCCESS[reviewable.type.toUpperCase()]
    return { reviewable, successMessage }
  },
  onFailure: (error) => {
    const defaultMessage = FLAG_MESSAGE.AGREE.FAIL
    return { errorMessage: error.message || defaultMessage }
  },
}))

export const disagreeWithFlag = createPatchRequest(
  'DISAGREE_WITH_FLAG',
  (id) => ({
    url: `/educator_portal/flags/${id}/disagree`,
    body: {
      flag: {
        resolutionNote: Types.FLAG_RESOLUTION_NOTES.DISAGREE,
      },
    },
    token: getPortalAuthToken(),
    onSuccess: (reviewable) => {
      const successMessage =
        FLAG_MESSAGE.DISAGREE.SUCCESS[reviewable.type.toUpperCase()]
      return { reviewable, successMessage }
    },
    onFailure: (error) => {
      const defaultMessage = FLAG_MESSAGE.DISAGREE.FAIL
      return { errorMessage: error.errors || defaultMessage }
    },
  })
)

export const fetchCommunitiesOfPractice = createRequest(
  'FETCH_COMMUNITIES_OF_PRACTICE',
  () => ({
    url: '/educator_portal/communities_of_practice',
    token: getPortalAuthToken(),
  })
)

export const fetchCommunityOfPractice = createRequest(
  'FETCH_COMMUNITY_OF_PRACTICE',
  (id) => ({
    url: `/educator_portal/communities_of_practice/${id}`,
    token: getPortalAuthToken(),
  })
)

export const createMembership = createPostRequest(
  'CREATE_MEMBERSHIP',
  ({ communityOfPracticeId }) => ({
    url: '/educator_portal/memberships',
    body: {
      membership: {
        communityOfPracticeId,
      },
    },
    token: getPortalAuthToken(),
    onSuccess: (userDetails) => {
      const successMessage = MEMBERSHIP_MESSAGE.CREATE.SUCCESS
      return { userDetails, successMessage }
    },
    onFailure: (error) => {
      const defaultMessage = MEMBERSHIP_MESSAGE.CREATE.FAIL
      return { errorMessage: error.errors || defaultMessage }
    },
  })
)

export const deleteMembership = createDeleteRequest(
  'DELETE_MEMBERSHIP',
  ({ membershipId }) => ({
    url: `/educator_portal/memberships/${membershipId}`,
    token: getPortalAuthToken(),
    onSuccess: (userDetails) => {
      const successMessage = MEMBERSHIP_MESSAGE.DELETE.SUCCESS
      return { userDetails, successMessage }
    },
    onFailure: (error) => {
      const defaultMessage = MEMBERSHIP_MESSAGE.DELETE.FAIL
      return { errorMessage: error.errors || defaultMessage }
    },
  })
)

export const fetchAnnouncements = createRequest(
  'FETCH_ANNOUNCEMENTS',
  (cop_id) => ({
    url: `/educator_portal/communities_of_practice/${cop_id}/announcements`,
    token: getPortalAuthToken(),
  })
)

export const createOrUpdateAnnouncement = createRequest(
  'CREATE_OR_UPDATE_ANNOUNCEMENT',
  (announcement, announcementId, communityOfPracticeId) => {
    const baseUrl = `/educator_portal/communities_of_practice/${communityOfPracticeId}/announcements`
    const method = announcementId ? 'PATCH' : 'POST'
    const url = announcementId ? `${baseUrl}/${announcementId}` : baseUrl

    return {
      url,
      method,
      body: { announcement: formatAnnouncementDate(announcement) },
      token: getPortalAuthToken(),
    }
  }
)

export const deleteAnnouncement = createDeleteRequest(
  'DELETE_ANNOUNCEMENT',
  ({ communityOfPracticeId, announcementId }) => ({
    url: `/educator_portal/communities_of_practice/${communityOfPracticeId}/announcements/${announcementId}`,
    token: getPortalAuthToken(),
    onSuccess: (announcements) => {
      const successMessage = ANNOUNCEMENT_MESSAGE.DELETE.SUCCESS
      return { announcements, successMessage }
    },
    onFailure: (error) => {
      const defaultMessage = MEMBERSHIP_MESSAGE.DELETE.FAIL
      return { errorMessage: error.errors || defaultMessage }
    },
  })
)

export const fetchAttachments = createRequest(
  'FETCH_ATTACHMENTS',
  ({ communityOfPracticeId }) => {
    return {
      url: `/educator_portal/communities_of_practice/${communityOfPracticeId}/attachments`,
      token: getPortalAuthToken(),
    }
  }
)

export const createOrUpdateAttachment = createRequest(
  'CREATE_OR_UPDATE_ATTACHMENT',
  ({ name, attachable, attachment, id }) => {
    const baseUrl = `educator_portal/attachments`
    const method = id ? 'PATCH' : 'POST'
    const url = id ? `${baseUrl}/${id}` : baseUrl

    const [attachableType, attachableId] = attachable.split('_')

    return {
      url,
      method,
      body: {
        uploadUrl: attachment.src,
        name,
        attachableType,
        attachableId,
      },
      token: getPortalAuthToken(),
    }
  }
)

export const deleteAttachment = createDeleteRequest(
  'DELETE_ATTACHMENT',
  (id) => ({
    url: `/educator_portal/attachments/${id}`,
    token: getPortalAuthToken(),
    onSuccess: (attachments) => {
      const successMessage = ATTACHMENT_MESSAGE.DELETE.SUCCESS
      return { attachments, successMessage }
    },
    onFailure: (error) => {
      const defaultMessage = ATTACHMENT_MESSAGE.DELETE.FAIL
      return { errorMessage: error.errors || defaultMessage }
    },
  })
)

export const fetchFolders = createRequest(
  'FETCH_FOLDERS',
  ({ communityOfPracticeId }) => {
    return {
      url: `/educator_portal/communities_of_practice/${communityOfPracticeId}/folders`,
      token: getPortalAuthToken(),
    }
  }
)

export const fetchFolder = createRequest(
  'FETCH_FOLDER',
  ({ communityOfPracticeId, folderId }) => {
    return {
      url: `/educator_portal/communities_of_practice/${communityOfPracticeId}/folders/${folderId}`,
      token: getPortalAuthToken(),
    }
  }
)

export const createFolder = createPostRequest(
  'CREATE_FOLDER',
  ({ name, folderable }) => {
    const [folderableType, folderableId] = folderable.split('_')

    return {
      url: '/educator_portal/folders',
      body: {
        name,
        folderableType,
        folderableId,
      },
      token: getPortalAuthToken(),
    }
  }
)

export const deleteFolder = createDeleteRequest('DELETE_FOLDER', (id) => ({
  url: `/educator_portal/folders/${id}`,
  token: getPortalAuthToken(),
  onSuccess: (folders) => {
    const successMessage = FOLDER_MESSAGE.DELETE.SUCCESS
    return { folders, successMessage }
  },
  onFailure: (error) => {
    const defaultMessage = FOLDER_MESSAGE.DELETE.FAIL
    return { errorMessage: error.errors || defaultMessage }
  },
}))

export const fetchAttachableOptions = createRequest(
  'FETCH_ATTACHABLE_OPTIONS',
  (communityOfPracticeId) => ({
    url: `/educator_portal/communities_of_practice/${communityOfPracticeId}/attachable_options`,
    token: getPortalAuthToken(),
  })
)

export const fetchFolderableOptions = createRequest(
  'FETCH_FOLDERABLE_OPTIONS',
  (communityOfPracticeId) => ({
    url: `/educator_portal/communities_of_practice/${communityOfPracticeId}/folderable_options`,
    token: getPortalAuthToken(),
  })
)

// Shared actions

export const signIn = createPostRequest(
  'SIGN_IN',
  ({ email, password, portal }) => {
    return {
      url: '/authentication/sessions',
      body: { session: { email, password }, portal },
      onFailure: (error) => {
        const redirectUrl = error.response.redirectUrl
        if (redirectUrl) {
          delayedRedirectToUrl(redirectUrl)
        }
        return {
          errors: normalizeRequestFormErrors(error.errors, {
            sensitiveAuth: true,
            sensitiveAuthFlow: 'signIn',
          }),
        }
      },
    }
  }
)

export const signUp = createPostRequest('SIGN_UP', (params) => {
  return {
    url: '/authentication/registrations',
    body: { registration: params },
    onFailure: (error) => ({
      errors: normalizeRequestFormErrors(error.errors, {
        sensitiveAuth: false,
      }),
    }),
  }
})

export const submitTeachingArtistApplication = createPostRequest(
  'SUBMIT_TEACHING_ARTIST_APPLICATION',
  (payload) => ({
    url: '/authentication/teaching_artist_applications',
    body: { teaching_artist_application: payload },
    onFailure: (error) => ({
      errors: normalizeRequestFormErrors(error?.errors, {
        sensitiveAuth: false,
      }),
    }),
  })
)

export const submitPartnerApplication = createPostRequest(
  'SUBMIT_PARTNER_APPLICATION',
  (payload) => ({
    url: '/authentication/partner_applications',
    body: { partner_application: payload },
    onFailure: (error) => ({
      errors: normalizeRequestFormErrors(error?.errors, {
        sensitiveAuth: false,
      }),
    }),
  })
)

export const signInViaSso = createPostRequest(
  'SIGN_IN_VIA_SSO',
  ({ type = 'saml', token, userId, employeeId, portal }) => {
    return {
      url: `/sso/${type}/authenticate`,
      body: { session: { userId, token, employeeId }, portal },
      onFailure: ({ response }) => ({ errors: { _error: response.message } }),
    }
  }
)

export const signInViaGoogleSso = createPostRequest(
  'SIGN_IN_VIA_GOOGLE_SSO',
  ({ credential }) => {
    return {
      url: `/authentication/sessions/create_google_session`,
      body: { credential },
      onFailure: (error) => {
        const redirectUrl = error.response.redirectUrl
        if (redirectUrl) {
          delayedRedirectToUrl(redirectUrl)
        }
        return {
          errors: normalizeRequestFormErrors(error.errors, {
            sensitiveAuth: true,
            sensitiveAuthFlow: 'signIn',
          }),
        }
      },
    }
  }
)

export const forgotPassword = createPostRequest(
  'FORGOT_PASSWORD',
  ({ portal, ...params }) => {
    return {
      url: '/authentication/passwords',
      body: { password: params, portal },
      onFailure: (error) => {
        const redirectUrl = error.response.redirectUrl
        if (redirectUrl) {
          delayedRedirectToUrl(redirectUrl)
        }
        return {
          errors: normalizeRequestFormErrors(error.errors, {
            sensitiveAuth: true,
            sensitiveAuthFlow: 'passwordRecovery',
          }),
        }
      },
    }
  }
)

export const resetPassword = createPatchRequest(
  'RESET_PASSWORD',
  ({ token, password }) => {
    return {
      url: '/authentication/passwords',
      body: {
        token,
        passwordReset: { password },
      },
    }
  }
)

export const claimAccount = createPostRequest('CLAIM_ACCOUNT', (params) => {
  return {
    url: '/authentication/claim_accounts',
    body: { password: params },
    onFailure: (error) => {
      const redirectUrl = error.response.redirectUrl
      if (redirectUrl) {
        delayedRedirectToUrl(redirectUrl)
      }
      return {
        errors: normalizeRequestFormErrors(error.errors, {
          sensitiveAuth: true,
          sensitiveAuthFlow: 'claim',
        }),
      }
    },
  }
})

export const updateCurrentUser = createPatchRequest(
  'UPDATE_CURRENT_USER',
  (params) => {
    return {
      url: `/authentication/users`,
      body: params,
      token: getPortalAuthToken(),
    }
  }
)

export const agreeToTerms = createPatchRequest('AGREE_TO_TERMS', () => {
  return {
    url: '/authentication/agree_to_terms',
    token: getPortalAuthToken(),
  }
})

export const refreshSession = createPostRequest('REFRESH_SESSION', () => ({
  url: '/authentication/sessions/refresh',
  body: {},
  token: getPortalAuthToken(),
}))

export const switchAccount = createPostRequest(
  'SWITCH_ACCOUNT',
  (employeeId) => {
    return {
      url: '/authentication/switch_accounts',
      body: { session: { employeeId } },
      token: getPortalAuthToken(),
    }
  }
)

export const markAllAsRead = createPatchRequest(
  'MARK_ALL_AS_READ',
  (notificationIds) => {
    return {
      url: 'notifications/mark_all_read',
      body: { notificationIds: notificationIds },
      token: getPortalAuthToken(),
    }
  }
)

export const markAsRead = createPatchRequest('MARK_AS_READ', (id) => ({
  url: `notifications/${id}/mark_read`,
  token: getPortalAuthToken(),
}))

export const deleteAllNotifications = createDeleteRequest(
  'DELETE_ALL_NOTIFICATIONS',
  (notificationIds) => ({
    url: 'notifications/destroy_all',
    body: { notificationIds: notificationIds },
    token: getPortalAuthToken(),
  })
)

export const deleteNotification = createDeleteRequest(
  'DELETE_NOTIFICATION',
  (id) => ({
    url: `/notifications/${id}`,
    token: getPortalAuthToken(),
  })
)

export const fetchSchoolTypes = createRequest('FETCH_SCHOOL_TYPES', () => ({
  url: '/school_categories',
}))

export const fetchDisciplines = createRequest('FETCH_DISCIPLINES', () => ({
  url: `/disciplines`,
}))

export const fetchApproaches = createRequest('FETCH_APPROACHES', () => ({
  url: `/approaches`,
}))

export const fetchObstacleTypes = createRequest('FETCH_OBSTACLE_TYPES', () => ({
  url: `/obstacles`,
}))

export const fetchStandards = createRequest('FETCH_STANDARDS', () => ({
  url: `/standards`,
}))

export const fetchGovernanceTypes = createRequest(
  'FETCH_GOVERNANCE_TYPES',
  () => ({
    url: `/governances`,
  })
)

export const fetchSchoolYears = createRequest('FETCH_SCHOOL_YEARS', () => ({
  url: `/school_years`,
}))

export const fetchNotifications = createRequest('FETCH_NOTIFICATIONS', () => ({
  url: '/notifications',
  token: getPortalAuthToken(),
}))

export const fetchNotificationPreferences = createRequest(
  'FETCH_NOTIFICATION_PREFERENCES',
  (subscriptionId) => ({
    url: `/notification_subscriptions/${subscriptionId}`,
    token: getPortalAuthToken(),
  })
)

export const updateNotificationPreferences = createPatchRequest(
  'UPDATE_NOTIFICATION_PREFERENCES',
  (subscriptionId, params) => ({
    url: `/notification_subscriptions/${subscriptionId}`,
    body: params,
    token: getPortalAuthToken(),
  })
)

export const fetchActiveSchoolYears = createRequest(
  'FETCH_ACTIVE_SCHOOL_YEARS',
  (params) => ({
    url: `/school_years`,
    query: params,
  })
)

export const fetchActiveAndPublishedSchoolYears = createRequest(
  'FETCH_ACTIVE_AND_PUBLISHED_SCHOOL_YEARS',
  ({ recent = true, ...params }) => ({
    url: `/school_years`,
    query: {
      ...params,
      published: true,
      recent,
    },
  })
)

export const fetchResourceTypes = createRequest('FETCH_RESOURCE_TYPES', () => ({
  url: `/resource_types`,
}))

export const fetchOutcomeTypes = createRequest('FETCH_OUTCOME_TYPES', () => ({
  url: `/outcome_types`,
  token: getPortalAuthToken(),
}))

export const fetchPositions = createRequest('FETCH_POSITIONS', () => ({
  url: `/positions`,
  token: getPortalAuthToken(),
}))

export const fetchGrades = createRequest('FETCH_GRADES', () => ({
  url: `/grades`,
  token: getPortalAuthToken(),
}))

export const fetchDemographics = createRequest('FETCH_DEMOGRAPHICS', () => ({
  url: `/demographics`,
}))

export const fetchFinancialAssistanceTypes = createRequest(
  'FETCH_FINANCIAL_ASSISTANCE_TYPES',
  () => ({
    url: `/financial_assistance_types`,
    token: getPortalAuthToken(),
  })
)

export const fetchLeadershipCharacteristics = createRequest(
  'FETCH_LEADERSHIP_CHARACTERISTICS',
  () => ({
    url: `/leadership_characteristics`,
    token: getPortalAuthToken(),
  })
)

export const fetchIdentityFrequencies = createRequest(
  'FETCH_IDENTITY_FREQUENCIES',
  () => ({
    url: `/identity_frequencies`,
    token: getPortalAuthToken(),
  })
)

export const fetchPartnershipQuestion = createRequest(
  'FETCH_PARTNERSHIP_QUESTION',
  () => ({
    url: '/partnership_question',
    token: getPortalAuthToken(),
  })
)
